import { useCallback, useEffect, useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Paper,
  Collapse,
  Box,
  Avatar,
  Badge,
  ActionIcon,
  TextInput,
  Alert,
  Loader,
  Center,
  Tooltip,
} from '@mantine/core';
import { formatDayOfWeek } from '../../../../utils/formatters';
import { getTimezoneAbbreviation } from '../../../../utils/timezone';
import { ChevronDown, ChevronRight, Edit2, Check, X } from 'react-feather';
import {
  sessionNumberConfigsApi,
  type BySessionGroupCoachingEntry,
  type BySessionGroupRow,
} from '../../../../services/api/sessionNumberConfigs';
import { formatDate } from '../../../../utils/formatters';
import { getGroupAvatarColor } from '../../../../utils/groupColors';
import { CheckInCollapsibleSection } from './CheckInCollapsibleSection';
import { useFeatureFlag } from '../../../../stores/featureFlags';
import type { CoachHuddle } from '../../../../types';
import { BuildContentButton } from './BuildContentButton';
import { SessionContentStatusBadge } from './SessionContentStatusBadge';

interface GroupCoachingBySessionViewProps {
  cohortId: string;
  /** Pass a coach's user id to scope the view to their assigned groups (Coach View). */
  coachUserId?: string;
  /** When true, hide edit affordances and coach-name column (Coach View). */
  readOnly?: boolean;
  /**
   * MED-1086 (E1.3m / AC2): per-modality live-session-delivery setting — the
   * second switch of e1.3l's gate for the Build Content entry point. Combined
   * with the global flag inside BuildContentButton. Defaults false (fail closed).
   */
  modalityLiveSessionDeliveryEnabled?: boolean;
  /** Check-in huddles for the cohort, rendered as Intro/Midpoint/Other accordions. */
  huddles?: CoachHuddle[];
  /** Total participants (for check-in progress denominators). */
  totalParticipants?: number;
  /** Click a group row → open the existing session detail slide-out. */
  onSessionClick?: (sessionId: string) => void;
  onHuddleClick?: (huddleId: string) => void;
  /** Bumped by the parent to trigger a refetch (e.g. after a detail-drawer mutation). */
  refreshKey?: number;
  /** Expose refetch to parent (e.g. for bulk-edit flow). */
  onLoaded?: (entries: BySessionGroupCoachingEntry[]) => void;
}

function percentOrDash(num: number, denom: number): string {
  if (denom <= 0) return '—';
  return `${Math.round((num / denom) * 100)}%`;
}

function StatusPill({ status }: { status: string }) {
  const { label, color } =
    status === 'completed'
      ? { label: 'Complete', color: 'medleyGreen' }
      : status === 'scheduled'
        ? { label: 'Scheduled', color: 'blue' }
        : status === 'cancelled'
          ? { label: 'Cancelled', color: 'gray' }
          : { label: 'Unscheduled', color: 'gray' };
  return (
    <Badge
      size="xs"
      color={color}
      variant="light"
      tt="uppercase"
      style={{ letterSpacing: '0.3px' }}
    >
      {label}
    </Badge>
  );
}

export function GroupCoachingBySessionView({
  cohortId,
  coachUserId,
  readOnly = false,
  modalityLiveSessionDeliveryEnabled = false,
  huddles = [],
  totalParticipants = 0,
  onSessionClick,
  onHuddleClick,
  refreshKey,
  onLoaded,
}: GroupCoachingBySessionViewProps) {
  const [entries, setEntries] = useState<BySessionGroupCoachingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [editingSn, setEditingSn] = useState<number | null>(null);
  const [editBuffer, setEditBuffer] = useState<string>('');
  const [saving, setSaving] = useState<number | null>(null);

  // MED-954 / E4.0d: peer-led cohorts swap the Coach column for a per-session
  // Leader column. Gated on live_session_delivery so it fails closed (AC8/IV8).
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await sessionNumberConfigsApi.getBySessionNumber(
        cohortId,
        'group_coaching',
        coachUserId,
      );
      if (res.modality !== 'group_coaching') {
        setEntries([]);
      } else {
        setEntries(res.session_numbers);
        onLoaded?.(res.session_numbers);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load by-session view');
    } finally {
      setLoading(false);
    }
  }, [cohortId, coachUserId, onLoaded]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const toggleExpanded = useCallback((sn: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(sn)) next.delete(sn);
      else next.add(sn);
      return next;
    });
  }, []);

  const beginEdit = useCallback((entry: BySessionGroupCoachingEntry) => {
    setEditingSn(entry.session_number);
    setEditBuffer(entry.session_number_topic ?? '');
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingSn(null);
    setEditBuffer('');
  }, []);

  const saveEdit = useCallback(
    async (entry: BySessionGroupCoachingEntry) => {
      const topicValue = editBuffer.trim() === '' ? null : editBuffer.trim();
      setSaving(entry.session_number);
      try {
        await sessionNumberConfigsApi.setGroupCoachingTopic(
          cohortId,
          entry.session_number,
          topicValue,
        );
        await load();
        setEditingSn(null);
        setEditBuffer('');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to save topic');
      } finally {
        setSaving(null);
      }
    },
    [cohortId, editBuffer, load],
  );

  // ----- Check-in accordion placement -----
  // AC20: Other/General Check-Ins after the last session.
  // MED-1350: intro and midpoint check-ins are 1:1 rows now and show in the
  // 1:1 section, so the huddle list holds general check-ins only.

  // AC8: Derive display topic per session from group-level computed topics.
  // If all groups share the same topic → show it; if they differ → comma-separated list.
  const getSessionDisplayTopic = useCallback((entry: BySessionGroupCoachingEntry): string | null => {
    // Prefer session-number-level topic if set
    if (entry.session_number_topic) return entry.session_number_topic;
    // Otherwise derive from group computed topics
    const uniqueTopics = [...new Set(
      entry.groups
        .map((g) => g.computed_topic)
        .filter((t): t is string => t != null)
    )];
    if (uniqueTopics.length === 0) return null;
    if (uniqueTopics.length === 1) return uniqueTopics[0];
    return uniqueTopics.join(', ');
  }, []);

  if (loading) {
    return (
      <Center p="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" variant="light" title="Could not load sessions">
        {error}
      </Alert>
    );
  }

  if (entries.length === 0) {
    return (
      <Paper p="lg" radius="md" withBorder>
        <Text size="sm" c="dimmed" ta="center">
          No group coaching sessions yet.
        </Text>
      </Paper>
    );
  }

  return (
    <Stack gap={0}>
      {entries.map((entry) => {
        const isOpen = expanded.has(entry.session_number);
        const isEditing = editingSn === entry.session_number;
        const derivedTopic = getSessionDisplayTopic(entry);

        return (
          <Box key={entry.session_number}>
            <Box
              style={{
                borderBottom: '1px solid var(--mantine-color-gray-2)',
              }}
            >
              <Box
                role="button"
                tabIndex={0}
                onClick={() => toggleExpanded(entry.session_number)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') toggleExpanded(entry.session_number);
                }}
                style={{ cursor: 'pointer' }}
              >
                <Group
                  justify="space-between"
                  wrap="nowrap"
                  style={{
                    padding: '16px 20px',
                    borderBottom: isOpen ? '1px solid var(--mantine-color-gray-2)' : 'none',
                  }}
                >
                  <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                    {isOpen ? (
                      <ChevronDown size={16} color="var(--mantine-color-dimmed)" />
                    ) : (
                      <ChevronRight size={16} color="var(--mantine-color-dimmed)" />
                    )}
                    {isEditing ? (
                      <Stack
                        gap={4}
                        style={{ flex: 1 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Group gap="xs" wrap="nowrap">
                          <Text fw={600} size="sm" style={{ flexShrink: 0 }}>
                            Session {entry.session_number}
                          </Text>
                          <TextInput
                            value={editBuffer}
                            onChange={(e) => setEditBuffer(e.currentTarget.value)}
                            placeholder={`Session ${entry.session_number} topic`}
                            size="sm"
                            style={{ flex: 1, maxWidth: 420 }}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEdit(entry);
                              if (e.key === 'Escape') cancelEdit();
                            }}
                            disabled={saving === entry.session_number}
                          />
                          <ActionIcon
                            variant="filled"
                            color="medleyGreen"
                            onClick={() => saveEdit(entry)}
                            loading={saving === entry.session_number}
                            aria-label="Save topic"
                          >
                            <Check size={14} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={cancelEdit}
                            aria-label="Cancel edit"
                            disabled={saving === entry.session_number}
                          >
                            <X size={14} />
                          </ActionIcon>
                        </Group>
                        {/* AC14: confirmation copy describing propagation */}
                        <Text size="xs" c="dimmed">
                          This topic will be applied to all {entry.groups_total} group
                          session{entry.groups_total === 1 ? '' : 's'} for Session{' '}
                          {entry.session_number}. Changing it here or from a session's topic
                          picker keeps both in sync.
                        </Text>
                      </Stack>
                    ) : (
                      <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                        <Text fw={600} size="sm" style={{ flexShrink: 0 }}>
                          Session {entry.session_number}
                        </Text>
                        {derivedTopic && (
                          <>
                            <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>—</Text>
                            <Text size="sm" fw={500} truncate>
                              {derivedTopic}
                            </Text>
                          </>
                        )}
                        {!readOnly && (
                          <Tooltip
                            label={
                              derivedTopic
                                ? 'Edit session topic'
                                : 'Add session topic'
                            }
                          >
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              color="gray"
                              onClick={(e) => {
                                e.stopPropagation();
                                beginEdit(entry);
                              }}
                              aria-label="Edit topic"
                            >
                              <Edit2 size={12} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        {/* MED-1086 (E1.3m / AC11): content-status badge on the
                            By-Session header — closes the gap where e1.3k shipped
                            on By-Date only. Immediately follows the identity. */}
                        <SessionContentStatusBadge contentStatus={entry.content_status} />
                        {/* MED-1086 (E1.3m / AC3, AC4): Build Content, rightmost of
                            the identity cluster. Groups exist here → open the
                            existing sessionId editor via the representative row. */}
                        {!readOnly && (
                          <BuildContentButton
                            cohortId={cohortId}
                            sessionNumber={entry.session_number}
                            sessionId={entry.groups[0]?.session_id ?? null}
                            modalityLiveSessionDeliveryEnabled={
                              modalityLiveSessionDeliveryEnabled
                            }
                          />
                        )}
                      </Group>
                    )}
                  </Group>

                  <Group gap="lg" wrap="nowrap" style={{ flexShrink: 0 }}>
                    <Stack gap={0} align="flex-end">
                      <Text size="xs" c="dimmed">
                        Week of
                      </Text>
                      <Text size="xs" fw={500}>
                        {entry.week_of ? formatDate(entry.week_of) : '—'}
                      </Text>
                    </Stack>
                    <Stack gap={0} align="flex-end">
                      <Text size="xs" c="dimmed">
                        Groups
                      </Text>
                      <Text size="xs" fw={500}>
                        {entry.groups_scheduled + entry.groups_complete}/{entry.groups_total}
                      </Text>
                    </Stack>
                    <Stack gap={0} align="flex-end">
                      <Text size="xs" c="dimmed">
                        Attendance
                      </Text>
                      <Text size="xs" fw={500}>
                        {percentOrDash(entry.attendance_attended, entry.attendance_total)}
                      </Text>
                    </Stack>
                    <Stack gap={0} align="flex-end">
                      <Text size="xs" c="dimmed">
                        Avg Engagement
                      </Text>
                      <Text size="xs" fw={500}>
                        {entry.avg_engagement != null ? entry.avg_engagement.toFixed(1) : '—'}
                      </Text>
                    </Stack>
                  </Group>
                </Group>
              </Box>

              <Collapse in={isOpen}>
                <Stack gap={0}>
                  {entry.groups.map((g) => (
                    <GroupRow
                      key={g.session_id}
                      row={g}
                      showCoach={!readOnly}
                      // MED-1181 / AC2: the peer-leader column is an LSD affordance —
                      // gate on the global flag AND the cohort's per-modality LSD
                      // toggle (reusing the same prop that gates Build Content), not
                      // the flag alone. Hidden for a classic MED01 cohort.
                      showLeader={liveSessionDeliveryEnabled && modalityLiveSessionDeliveryEnabled}
                      onClick={() => onSessionClick?.(g.session_id)}
                    />
                  ))}
                </Stack>
              </Collapse>
            </Box>
          </Box>
        );
      })}

      {/* AC20: Other/General Check-Ins — always rendered after last session */}
      <CheckInCollapsibleSection
        huddles={huddles}
        totalParticipants={totalParticipants}
        onHuddleClick={onHuddleClick}
      />
    </Stack>
  );
}

interface GroupRowProps {
  row: BySessionGroupRow;
  showCoach: boolean;
  /**
   * MED-954 / E4.0d: when true and the row is peer-led, render a per-session
   * Leader column in place of the Coach column. Driven by the
   * live_session_delivery feature flag in the parent.
   */
  showLeader: boolean;
  onClick?: () => void;
}

function formatTimeSlotDisplay(row: BySessionGroupRow): string | null {
  if (!row.time_slot_day_of_week || !row.time_slot_time) return null;
  const dayStr = formatDayOfWeek(row.time_slot_day_of_week);
  // Format time: "09:00:00" → "9:00 AM"
  const [hours, minutes] = row.time_slot_time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const timeStr = `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  const tzAbbr = row.time_slot_timezone
    ? getTimezoneAbbreviation(row.time_slot_timezone)
    : '';
  return `${dayStr} ${timeStr}${tzAbbr ? ` ${tzAbbr}` : ''}`;
}

function GroupRow({ row, showCoach, showLeader, onClick }: GroupRowProps) {
  const color = getGroupAvatarColor(row.group_name, row.group_id);
  const timeSlotDisplay = formatTimeSlotDisplay(row);
  // MED-954 / E4.0d AC5: peer-led rows show the per-session Leader (or
  // "Unassigned") instead of the Coach column. Coach-led rows are unchanged.
  const showLeaderColumn = showLeader && row.is_peer_led;
  return (
    <Group
      justify="space-between"
      p="sm"
      wrap="nowrap"
      style={{
        cursor: onClick ? 'pointer' : 'default',
        borderTop: '1px solid var(--mantine-color-gray-1)',
      }}
      onClick={onClick}
    >
      <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
        <Avatar size="sm" radius="xl" style={{ backgroundColor: color, color: '#fff' }}>
          {row.group_name.charAt(0).toUpperCase()}
        </Avatar>
        <Text size="sm" fw={500} truncate>
          {row.group_name}
        </Text>
        {timeSlotDisplay && (
          <Badge
            variant="light"
            color="gray"
            size="lg"
            radius="xl"
            styles={{ label: { fontWeight: 500, fontSize: 13, textTransform: 'none', letterSpacing: 0 } }}
          >
            {timeSlotDisplay}
          </Badge>
        )}
        <StatusPill status={row.status} />
      </Group>
      <Group gap="lg" wrap="nowrap" style={{ flexShrink: 0 }}>
        <Stack gap={0} align="flex-end">
          <Text size="xs" c="dimmed">
            Attended
          </Text>
          <Text size="xs" fw={500}>
            {row.attendance_attended}/{row.attendance_total}
          </Text>
        </Stack>
        <Stack gap={0} align="flex-end">
          <Text size="xs" c="dimmed">
            Engagement
          </Text>
          <Text size="xs" fw={500}>
            {row.avg_engagement != null ? row.avg_engagement.toFixed(1) : '—'}
          </Text>
        </Stack>
        {showLeaderColumn ? (
          <Stack gap={0} align="flex-end" miw={120}>
            <Text size="xs" c="dimmed">
              Leader
            </Text>
            <Text size="xs" fw={500} truncate>
              {row.session_leader_name ?? 'Unassigned'}
            </Text>
          </Stack>
        ) : (
          showCoach && (
            <Stack gap={0} align="flex-end" miw={120}>
              <Text size="xs" c="dimmed">
                Coach
              </Text>
              <Text size="xs" fw={500} truncate>
                {row.coach_name ?? '—'}
              </Text>
            </Stack>
          )
        )}
      </Group>
    </Group>
  );
}

export default GroupCoachingBySessionView;
